import { useState } from 'react';
import { ContentCopy } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@mui/material';

import { useCopyReferralLink } from '../store/hooks';
import { formatRewardAmount, getReferralRewardText } from '../utils';

interface HowReferralsWorkModalProps {
  open: boolean;
  onClose: () => void;
  referralCode?: string;
  referrerRewardAmount?: number | null;
  signupDiscountAmount?: number | null;
}

/**
 * Modal explaining how the referral program works.
 * Displays a 3-step process matching the ReferralDashboard content.
 * Optionally shows the referral code with copy functionality.
 */
export const HowReferralsWorkModal = ({
  open,
  onClose,
  referralCode,
  referrerRewardAmount,
  signupDiscountAmount,
}: HowReferralsWorkModalProps) => {
  const { copyLink } = useCopyReferralLink();
  const [copied, setCopied] = useState(false);
  const rewardText = getReferralRewardText(referrerRewardAmount, signupDiscountAmount);

  const referralSteps = [
    {
      number: 1,
      title: 'Share your referral link',
      description: 'Send your link or code to a colleague.',
    },
    {
      number: 2,
      title: 'They start a free trial',
      description: 'They use your link (code auto-applied) or enter your code during sign-up.',
    },
    {
      number: 3,
      title: rewardText.headline,
      description: rewardText.symmetric
        ? `Once they subscribe, you both get ${formatRewardAmount(rewardText.referrerAmount)} credit on your next invoice.`
        : `Once they subscribe, you get ${formatRewardAmount(rewardText.referrerAmount)} credit and they get ${formatRewardAmount(rewardText.signupAmount)} off on their next invoice.`,
    },
  ];

  const handleCopyLink = async () => {
    if (!referralCode) return;

    const success = await copyLink(referralCode, 'how_referrals_modal');
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle sx={{ fontWeight: 600 }}>How Referrals Work</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {referralSteps.map(step => (
            <Grid size={{ xs: 12 }} key={step.number}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '1rem',
                    flexShrink: 0,
                  }}
                >
                  {step.number}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {step.number === 3 ? (
                      rewardText.symmetric ? (
                        <>
                          You both earn{' '}
                          <Box component="span" sx={{ color: 'primary.main' }}>
                            {formatRewardAmount(rewardText.referrerAmount)}
                          </Box>
                        </>
                      ) : (
                        <>
                          You earn{' '}
                          <Box component="span" sx={{ color: 'primary.main' }}>
                            {formatRewardAmount(rewardText.referrerAmount)}
                          </Box>
                          , they get{' '}
                          <Box component="span" sx={{ color: 'primary.main' }}>
                            {formatRewardAmount(rewardText.signupAmount)}
                          </Box>{' '}
                          off
                        </>
                      )
                    ) : (
                      step.title
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Referral Code Section */}
        {referralCode && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: 1,
              borderColor: 'grey.200',
              mt: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: 'primary.main',
                flex: 1,
              }}
            >
              {referralCode}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleCopyLink}
              startIcon={<ContentCopy />}
              sx={{ flexShrink: 0 }}
            >
              {copied ? 'Copied!' : 'Copy link'}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};
